'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';

import { SimpleLinePulseChart } from '../../../../shared/simple-line-pulse-chart';
import { TrendChart, type ChartDataPoint } from '../../../../shared/trend-chart';
import { getNumberColorWithNegativeWhite } from '../../../shared/color';
import { CumulativeEarningsTooltipContent } from '../../../shared/cumulative-earnings-tooltip-content';
import { defiYieldsOverviewBunja } from '../../../shared/state';
import { EarningsFluctuationTooltip } from './earnings-fluctuation-tooltip';

export const Overview = () => {
  const { totalSubscribedAtom, cumulativeEarningsAtom, isChartVisibleAtom, chartDataAtom } =
    useBunja(defiYieldsOverviewBunja);
  const totalSubscribed = useAtomValue(totalSubscribedAtom);
  const cumulativeEarnings = useAtomValue(cumulativeEarningsAtom);
  const isChartVisible = useAtomValue(isChartVisibleAtom);
  const chartData = useAtomValue(chartDataAtom);
  const [expanded, setExpanded] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<ChartDataPoint | null>(null);

  const miniChartValues = useMemo(() => chartData.map((d: ChartDataPoint) => d.value), [chartData]);

  const todayUTCLabel = useMemo(() => {
    const now = new Date();
    const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(now.getUTCDate()).padStart(2, '0');
    return `(${mm}.${dd} UTC)`;
  }, []);

  const displayDateLabel = useMemo(() => {
    if (!hoveredPoint) return todayUTCLabel;
    const [, month, day] = hoveredPoint.date.split('-');
    return `(${month}.${day} UTC)`;
  }, [hoveredPoint, todayUTCLabel]);

  const displayValue = hoveredPoint ? String(hoveredPoint.value) : cumulativeEarnings;

  const todayDateStr = chartData.length > 0 ? chartData[chartData.length - 1].date : '';
  const isHoveringToday = Boolean(hoveredPoint && todayDateStr && hoveredPoint.date === todayDateStr);
  const fromPreviousDay =
    chartData.length >= 2 ? chartData[chartData.length - 1].value - chartData[chartData.length - 2].value : null;

  const handleHover = useCallback((point: ChartDataPoint | null) => {
    setHoveredPoint(point);
  }, []);

  return (
    <section className={css({ mt: 64 })}>
      <div
        className={flex({
          px: 24,
          py: 20,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
          rounded: 8,
          gap: 56,
          '& > div': {
            flex: 1,
          },
          tabletDown: {
            flexDirection: 'column',
            gap: 16,
          },
        })}
      >
        {/* total subscribed balance */}
        <div className={stack({ gap: 4 })}>
          <div className={hstack({ h: 24 })}>
            <p
              className={css({
                h: 24,
                textStyle: 'Text/M/Monospace/Regular',
                color: 'content.default.level-2',
              })}
            >
              <FormattedMessage id='Earn.r251204.Total_subscribed_balance' />
            </p>
          </div>
          <p
            className={css({
              textStyle: 'Title/S/Monospace',
              color: 'content.default.level-5',
            })}
          >
            <Formatted.Number value={totalSubscribed} suffix={UsdtCurrency} precision={UsdtPrecision} toFixed />
          </p>
        </div>

        {/* cumulative earnings */}
        <div
          className={stack({
            gap: 4,
            tabletDown: {
              pt: 12,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderColor: 'border.default.level-1',
            },
          })}
        >
          <div className={flex({ justify: 'space-between' })}>
            <div className={stack({ gap: 4 })}>
              <div
                className={stack({
                  tabletDown: { flexDirection: 'column' },
                  tablet: { flexDirection: 'row', alignItems: 'center', gap: 4 },
                })}
              >
                <Tooltip.Root placement='right' maxWidth={280}>
                  <Tooltip.TextTrigger label={<FormattedMessage id='Earn.r260212.Cumulative_earnings' />} />
                  <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r260212.Cumulative_earnings' />}>
                    <CumulativeEarningsTooltipContent />
                  </Tooltip.Content>
                </Tooltip.Root>
                <p
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                    color: 'content.default.level-2',
                  })}
                >
                  {expanded ? displayDateLabel : todayUTCLabel}
                </p>
              </div>
              <div className={hstack({ gap: 4 })}>
                <p className={css({ textStyle: 'Title/S/Monospace' })}>
                  <Formatted.Number
                    value={displayValue}
                    withPlus
                    css={{ color: getNumberColorWithNegativeWhite(displayValue) }}
                    precision={UsdtPrecision}
                    toFixed
                    suffix={UsdtCurrency}
                  />
                </p>
                {isChartVisible && (
                  <IconButton
                    icon={
                      <ChevronDownIcon
                        className={css({
                          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        })}
                      />
                    }
                    hierarchy='tertiary'
                    size='small'
                    onClick={() => setExpanded((prev) => !prev)}
                  />
                )}
              </div>
              {/* Fixed-height "from previous day" – only when expanded and hovered point is today */}
              {isChartVisible && expanded && (
                <div
                  className={css({
                    h: 20,
                    minH: 20,
                    display: 'flex',
                    alignItems: 'center',
                  })}
                >
                  {isHoveringToday && fromPreviousDay !== null && (
                    <p
                      className={css({
                        textStyle: 'Text/S/Monospace/Regular',
                        color: 'content.default.level-5',
                      })}
                    >
                      <FormattedMessage
                        id='Earn.r260212.Pnl_from_previous_day'
                        values={{
                          pnl: (
                            <Formatted.Number
                              key='pnl'
                              value={fromPreviousDay}
                              withPlus
                              suffix={UsdtCurrency}
                              precision={UsdtPrecision}
                              toFixed
                            />
                          ),
                        }}
                      />
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className={css({ w: 95, h: 50, tabletDown: { alignSelf: 'center' } })}>
              {isChartVisible && !expanded && (
                <SimpleLinePulseChart type='mini' data={miniChartValues} width={95} height={50} />
              )}
            </div>
          </div>
          {isChartVisible && expanded && (
            <div className={css({ mt: 16, h: 220 })}>
              <TrendChart data={chartData} height={220} onHover={handleHover} />
            </div>
          )}
        </div>
      </div>
      <EarningsFluctuationTooltip />
    </section>
  );
};
